<template>
  <div class="chart-container">
    <h3 class="title is-6">Monthly Performance Trends</h3>
    <div class="chart-wrapper">
      <canvas ref="trendsChart"></canvas>
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

export default {
  name: 'PerformanceTrendsChart',
  props: {
    data: {
      type: Object,
      default: () => ({ submission_trend: [] })
    }
  },
  data() {
    return {
      chart: null
    }
  },
  mounted() {
    // Add a small delay to ensure data is available and DOM is stable
    setTimeout(() => {
      this.$nextTick(() => {
        this.createChart();
      });
    }, 100);
  },
  watch: {
    data: {
      deep: true,
      handler() {
        // Add delay to ensure data is stable before updating
        setTimeout(() => {
          this.$nextTick(() => {
            this.updateChart();
          });
        }, 50);
      }
    }
  },
  beforeUnmount() {
    this.destroyChart();
  },
  methods: {
    createChart() {
      // Always destroy existing chart first to prevent conflicts
      this.destroyChart();
      
      // Validate data first
      if (!this.data || !this.data.submission_trend || !Array.isArray(this.data.submission_trend) || this.data.submission_trend.length === 0) {
        console.log('PerformanceTrendsChart: No valid data available for chart creation');
        return;
      }

      if (!this.$refs.trendsChart) {
        console.log('PerformanceTrendsChart: Canvas ref not available');
        return;
      }
      
      try {
        const canvas = this.$refs.trendsChart;
        
        // Ensure canvas is properly mounted in DOM
        if (!canvas.parentNode) {
          console.log('PerformanceTrendsChart: Canvas not mounted in DOM');
          return;
        }
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          console.log('PerformanceTrendsChart: Could not get canvas context');
          return;
        }
        
        const chartData = this.data.submission_trend;
        
        const labels = chartData.map(item => item.month) || [];
        const submissions = chartData.map(item => item.total_submissions) || [];
        const validated = chartData.map(item => item.validated_count) || [];
        const successRates = chartData.map(item => item.success_rate_percentage) || [];
        
        // Validate we have actual data
        if (labels.length === 0) {
          console.log('PerformanceTrendsChart: No labels available');
          return;
        }
        
        console.log('PerformanceTrendsChart: Creating chart with data:', { labels, submissions, validated, successRates });
        
        this.chart = new Chart(ctx, {
          type: 'line',
          data: {
            labels: labels,
            datasets: [
              {
                label: 'Total Submissions',
                data: submissions,
                borderColor: '#0a3954',
                backgroundColor: 'rgba(10, 57, 84, 0.1)',
                yAxisID: 'y'
              },
              {
                label: 'Validated Count',
                data: validated,
                borderColor: '#082B3F',
                backgroundColor: 'rgba(8, 43, 63, 0.1)',
                yAxisID: 'y'
              },
              {
                label: 'Success Rate (%)',
                data: successRates,
                borderColor: '#364b5c',
                backgroundColor: 'rgba(54, 75, 92, 0.1)',
                yAxisID: 'y1'
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false, // Disable all animations to prevent context conflicts
            interaction: {
              mode: 'index',
              intersect: false,
            },
            plugins: {
              legend: {
                position: 'top',
              }
            },
            scales: {
              x: {
                display: true,
                title: {
                  display: true,
                  text: 'Month'
                }
              },
              y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                  display: true,
                  text: 'Count'
                }
              },
              y1: {
                type: 'linear',
                display: true,
                position: 'right',
                title: {
                  display: true,
                  text: 'Success Rate (%)'
                },
                grid: {
                  drawOnChartArea: false,
                },
              }
            }
          }
        });
        
        console.log('PerformanceTrendsChart: Chart created successfully');
      } catch (error) {
        console.error('PerformanceTrendsChart: Error creating chart:', error);
      }
    },
    updateChart() {
      if (!this.chart) {
        this.createChart();
        return;
      }
      
      try {
        const chartData = this.data && this.data.submission_trend ? this.data.submission_trend : [];
        
        const labels = chartData.map(item => item.month) || [];
        const submissions = chartData.map(item => item.total_submissions) || [];
        const validated = chartData.map(item => item.validated_count) || [];
        const successRates = chartData.map(item => item.success_rate_percentage) || [];
        
        this.chart.data.labels = labels;
        this.chart.data.datasets[0].data = submissions;
        this.chart.data.datasets[1].data = validated;
        this.chart.data.datasets[2].data = successRates;
        this.chart.update();
      } catch (error) {
        console.error('Error updating trends chart:', error);
        this.destroyChart();
        this.$nextTick(() => {
          this.createChart();
        });
      }
    },
    destroyChart() {
      if (this.chart) {
        try {
          // Stop any ongoing animations before destroying
          this.chart.stop();
          this.chart.destroy();
          console.log('PerformanceTrendsChart: Chart destroyed successfully');
        } catch (error) {
          console.error('PerformanceTrendsChart: Error destroying chart:', error);
        } finally {
          this.chart = null;
        }
      }
    }
  }
}
</script>

<style scoped>
.chart-container {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.chart-wrapper {
  position: relative;
  height: 400px;
}
</style>